import * as React from 'react';
import { observer, Provider } from 'mobx-react';
import CSSTransition from 'react-transition-group/CSSTransition';
import { WebGivingMainViewModel } from './webgiving-main-view-model';
import { Router, WebGivingPage } from '../pages';
import { WebGivingMainHeader } from './header/webgiving-main-header';
import { HeroImage } from '../components/hero-image/hero-image';
import { WebGivingMainFooter } from './webgiving-main-footer';
import { Panel, PanelType } from '../components/panel';
import { BrandingTheme } from '../branding/branding-theme';
import { WebGivingMainBanner } from './banner/webgiving-main-banner';
import { generateManageAccountUrl } from '../utils/identity-utils';
import { NotificationController } from '../components/notification/notification';
import { classNames } from '../../Shared/utils/classnames';
import { ModalCommodore } from '../modals/modal-commodore';
import { getInjectablePropsForProvider } from '../utils/injectable-props';
import { ErrorBoundary } from '../../Shared/components/error-boundary';
import {
	main,
	container,
	eclipsed,
	overlay,
	transitionDurationMs,
	transitionDurationLongMs,
	loggedIn,
	loading,
	overlayEnter,
	overlayEnterActive,
	overlayExitActive,
	fadeEnter,
	fadeEnterActive,
	fadeExitActive,
	header,
	confettiWrapper,
} from './webgiving-main.less';
import 'polyfill-array-includes';
import { ConfettiAnimation } from '../../Shared/components/confetti/confetti';
import { EvidentlyOverridesWidget } from '../evidently';
import { panel as pageGivePanel, footer as pageGiveFooter } from '../pages/page-give/page-give.less';
import { ShouldTranslateProvider } from '../translation';

declare const STORYBOOK: boolean;

@observer
export class WebGivingMain extends React.Component<{ vm: WebGivingMainViewModel }> {
	render() {
		const {
			raiseAction,
			person,
			currentPageViewModel,
			theme,
			scrollStateStore,
			isAccountDetailsOpen,
			webGivingPushpayInfo,
			isAppLoading,
			viewData: { MerchantInfo: merchant, BrandingPackage: brandingPackage },
			sagaContext: { appState },
			hideAnimation,
			animationType,
			notification,
			hasTranslationEnabled,
			unavailableLanguages,
			uiConfiguration,
			setAppLoading,
			failedToLoadLanguage,
			setCurrentPageShouldBeVisible,
			setIsAccountDetailsOpen,
			reportError,
		} = this.props.vm;
		const pageIsGive = currentPageViewModel.type === WebGivingPage.Give;
		const showPayerCoverFeesLegalText =
			currentPageViewModel.type === WebGivingPage.ConfirmGift && merchant.SupportsPayerCoversFees;
		const overlayClassNames = {
			appear: overlayEnter,
			appearActive: overlayEnterActive,
			enter: overlayEnter,
			enterActive: overlayEnterActive,
			exitActive: overlayExitActive,
		};
		const fadeTransitionClassNames = {
			appear: fadeEnter,
			appearActive: fadeEnterActive,
			enter: fadeEnter,
			enterActive: fadeEnterActive,
			exitActive: fadeExitActive,
		};
		const heroImageUrl = brandingPackage && brandingPackage.HeroImageUrl;
		const transparentBackground = !!heroImageUrl && pageIsGive && scrollStateStore.isAtTopOfThePage;

		return (
			<ShouldTranslateProvider hasEntitlement={hasTranslationEnabled}>
				<Provider {...getInjectablePropsForProvider(raiseAction)}>
					<div>
						<ConfettiAnimation className={confettiWrapper} type={animationType} onComplete={hideAnimation} />
						<BrandingTheme theme={theme} />
						{heroImageUrl && (
							<ErrorBoundary onError={reportError}>
								<CSSTransition
									in={pageIsGive}
									classNames={fadeTransitionClassNames}
									timeout={parseInt(transitionDurationLongMs, 10)}
									appear
									unmountOnExit
								>
									<HeroImage heroImageUrl={heroImageUrl} />
								</CSSTransition>
							</ErrorBoundary>
						)}
						<ErrorBoundary onError={reportError}>
							<WebGivingMainHeader
								person={person}
								brandingPackage={brandingPackage}
								merchant={merchant}
								setIsAccountDetailsOpen={setIsAccountDetailsOpen}
								isAccountVisible={isAccountDetailsOpen}
								manageAccountUrl={generateManageAccountUrl(webGivingPushpayInfo, person)}
								setAppLoading={setAppLoading}
								hasTranslationEnabled={hasTranslationEnabled}
								unavailableLanguages={unavailableLanguages}
								failedToLoadLanguage={failedToLoadLanguage}
								onError={reportError}
								transparentBackground={transparentBackground}
								hideAccount={uiConfiguration.HideAccount}
								hideI18n={uiConfiguration.HideI18n}
								className={classNames(header, {
									[eclipsed]: isAccountDetailsOpen,
									[loggedIn]: !!person,
								})}
							/>
						</ErrorBoundary>
						<main
							className={classNames(main, {
								[eclipsed]: isAccountDetailsOpen,
								[loggedIn]: !!person,
								[`${loading} brand-primary-before-color`]: isAppLoading,
							})}
						>
							<ErrorBoundary onError={reportError}>
								<WebGivingMainBanner
									visible={pageIsGive}
									className={heroImageUrl ? '' : 'brand-secondary-bg'}
								/>
							</ErrorBoundary>
							<ErrorBoundary onError={reportError}>
								<NotificationController viewModel={notification} />
							</ErrorBoundary>
							<div className={`container ${container}`}>
								<Panel
									className={pageIsGive ? pageGivePanel : ''}
									panelType={pageIsGive ? PanelType.White : PanelType.Highlight}
									responsive
								>
									<Router
										currentPage={currentPageViewModel}
										setCurrentPageShouldBeVisible={setCurrentPageShouldBeVisible}
										onError={reportError}
									/>
								</Panel>
							</div>
							<ErrorBoundary onError={reportError}>
								<WebGivingMainFooter
									merchant={merchant}
									pushpayInfo={webGivingPushpayInfo}
									className={pageIsGive ? pageGiveFooter : ''}
									showPayerCoverFeesLegalText={showPayerCoverFeesLegalText}
								/>
							</ErrorBoundary>
						</main>
						<CSSTransition
							in={isAccountDetailsOpen}
							classNames={overlayClassNames}
							timeout={parseInt(transitionDurationMs, 10)}
							appear
							unmountOnExit
						>
							<div className={`${overlay} visible-xs`}></div>
						</CSSTransition>
						<ErrorBoundary onError={reportError}>
							<ModalCommodore vm={this.props.vm} appState={appState} onError={reportError} />
						</ErrorBoundary>
						{NewFeatures.SetupABTesting && NewFeatures.TesterCommandsEnabled && !STORYBOOK && (
							<ErrorBoundary onError={reportError}>
								<React.Suspense fallback={null}>
									<EvidentlyOverridesWidget />
								</React.Suspense>
							</ErrorBoundary>
						)}
					</div>
				</Provider>
			</ShouldTranslateProvider>
		);
	}
}
